import { create } from 'zustand';
import { preloadService, PreloadData, PreloadOptions } from '../services/preloadService';
import { authService } from '../features/auth/services/authService';

interface PreloadState {
  // Data state
  data: PreloadData | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  lastUpdated: number | null;

  // Actions
  initializePreload: (options?: PreloadOptions) => Promise<void>;
  refreshPreload: (options?: PreloadOptions) => Promise<void>;
  clearPreload: () => void;
  setError: (error: string | null) => void;
  
  // Smart getters (with fallbacks)
  getAvailableJobTypes: (sourcePlatformId: number, targetPlatformId: number) => any[];
  getAvailableJobTypesWithVirtual: (sourcePlatformId: number, targetPlatformId: number) => any[];
  getJobTypesByPlatform: (platformId: number, isSource?: boolean) => any[];
  getExistingSchema: (jobTypeId: number) => any | null;
  getExistingSchemas: (jobTypeId: number) => any[];
  getSchemaIdForJobType: (jobTypeId: number) => number | null;
  getEndpointsByPlatform: (platformId: number) => any[];
  getPlatformById: (platformId: number) => any | null;
  getJobTypeById: (jobTypeId: number) => any | null;
  validatePlatformJobTypeRelationship: (sourcePlatformId: number, targetPlatformId: number, jobTypeId: number) => boolean;
  
  // Individual data accessors
  getPlatforms: () => any[];
  getJobTypes: () => any[];
  getGroups: () => any[];
  getUserInfo: () => any | null;
  getSchemas: () => any[];
  getYamlConfigs: () => any[];
  getApiEndpoints: () => any[];
  getExtractors: () => any[];
  getLoaders: () => any[];
}

// Global promise to prevent concurrent preload requests
let preloadPromise: Promise<void> | null = null;

export const usePreloadStore = create<PreloadState>((set, get) => ({
  // Initial state
  data: null,
  isLoading: false,
  isInitialized: false,
  error: null,
  lastUpdated: null,

  // Actions
  initializePreload: async (options?: PreloadOptions) => {
    const state = get();
    
    // Don't initialize if user is not authenticated
    if (!authService.isAuthenticated()) {
      console.log('Skipping preload - user not authenticated');
      return;
    }
    
    // Don't reinitialize if already done and cache is valid
    if (state.isInitialized && preloadService.isCacheValid()) {
      return;
    }

    // Return existing promise if preload is already in progress
    if (preloadPromise) {
      return preloadPromise;
    }

    // Create new preload promise
    preloadPromise = (async () => {
      set({ isLoading: true, error: null });

      try {
        const data = await preloadService.preloadEssentials(options);
        set({ 
          data, 
          isLoading: false, 
          isInitialized: true, 
          error: null,
          lastUpdated: Date.now()
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to preload data';
        set({ 
          isLoading: false, 
          error: errorMessage,
          isInitialized: false 
        });
        throw error;
      } finally {
        // Clear the promise when done
        preloadPromise = null;
      }
    })();

    return preloadPromise;
  },

  refreshPreload: async (options?: PreloadOptions) => {
    // Clear any existing preload promise to force a fresh request
    preloadPromise = null;
    
    set({ isLoading: true, error: null });

    try {
      // Clear cache to force fresh data
      preloadService.clearCache();
      const data = await preloadService.preloadEssentials(options);
      set({ 
        data, 
        isLoading: false, 
        error: null,
        lastUpdated: Date.now()
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to refresh preload data';
      set({ 
        isLoading: false, 
        error: errorMessage 
      });
      throw error;
    }
  },

  clearPreload: () => {
    // Clear the promise to allow fresh initialization
    preloadPromise = null;
    preloadService.clearCache();
    set({
      data: null,
      isLoading: false,
      isInitialized: false,
      error: null,
      lastUpdated: null
    });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  // Smart getters with fallbacks
  getAvailableJobTypes: (sourcePlatformId: number, targetPlatformId: number) => {
    const state = get();
    if (!state.data) return [];
    return state.data.jobTypes.filter((jt: any) =>
      jt.source_platform_id === sourcePlatformId &&
      jt.target_platform_id === targetPlatformId
    );
  },

  getAvailableJobTypesWithVirtual: (sourcePlatformId: number, targetPlatformId: number) => {
    const state = get();
    if (!state.data) return [];
    
    return state.data.jobTypes.filter((jt: any) =>
      jt.source_platform_id === sourcePlatformId &&
      jt.target_platform_id === targetPlatformId
    );
  },

  getJobTypesByPlatform: (platformId: number, isSource: boolean = true) => {
    const state = get();
    if (!state.data) return [];
    const field = isSource ? 'source_platform_id' : 'target_platform_id';
    return state.data.jobTypes.filter((jt: any) => jt[field] === platformId);
  },

  getExistingSchema: (jobTypeId: number) => {
    const state = get();
    if (!state.data) return null;
    
    // First try to get schemas from the job type itself
    const jobType = state.data.jobTypes.find((jt: any) => jt.id === jobTypeId);
    if (jobType?.schemas && jobType.schemas.length > 0) {
      const schemaId = typeof jobType.schemas[0] === 'string' ? parseInt(jobType.schemas[0]) : jobType.schemas[0];
      return state.data.schemas.find((s: any) => s.id === schemaId && s.is_active) || null;
    }
    
    // Fallback to filtering by job_type field
    return state.data.schemas.find((s: any) => 
      s.job_type === jobTypeId && s.is_active
    ) || null;
  },

  getExistingSchemas: (jobTypeId: number) => {
    const state = get();
    if (!state.data) return [];
    
    // First try to get schemas from the job type itself
    const jobType = state.data.jobTypes.find((jt: any) => jt.id === jobTypeId);
    if (jobType?.schemas && jobType.schemas.length > 0) {
      const schemaIds = jobType.schemas.map((s: any) => typeof s === 'string' ? parseInt(s) : s);
      return state.data.schemas.filter((s: any) => 
        schemaIds.includes(s.id) && s.is_active
      );
    }
    
    // Fallback to filtering by job_type field
    return state.data.schemas.filter((s: any) => 
      s.job_type === jobTypeId && s.is_active
    );
  },

  getSchemaIdForJobType: (jobTypeId: number) => {
    const state = get();
    if (!state.data) return null;
    
    // First try to get schemas from the job type itself
    const jobType = state.data.jobTypes.find((jt: any) => jt.id === jobTypeId);
    if (jobType?.schemas) {
      // Handle both string and array formats from different API versions
      let schemaId: number;
      
      if (typeof jobType.schemas === 'string') {
        // Mock server format: schemas is a string that might contain ID
        const parsedId = parseInt(jobType.schemas);
        if (!isNaN(parsedId)) {
          schemaId = parsedId;
        } else {
          // If string doesn't contain valid ID, fallback to job_type field
          const schema = state.data.schemas.find((s: any) => s.job_type === jobTypeId && s.is_active);
          return schema ? schema.id : null;
        }
      } else if (Array.isArray(jobType.schemas) && jobType.schemas.length > 0) {
        // Production format: schemas is an array
        schemaId = typeof jobType.schemas[0] === 'string' ? parseInt(jobType.schemas[0]) : jobType.schemas[0];
      } else {
        // Fallback to job_type field
        const schema = state.data.schemas.find((s: any) => s.job_type === jobTypeId && s.is_active);
        return schema ? schema.id : null;
      }
      
      const schema = state.data.schemas.find((s: any) => s.id === schemaId && s.is_active);
      return schema ? schemaId : null;
    }
    
    // Fallback to filtering by job_type field
    const schema = state.data.schemas.find((s: any) => 
      s.job_type === jobTypeId && s.is_active
    );
    return schema ? schema.id : null;
  },

  getEndpointsByPlatform: (platformId: number) => {
    const state = get();
    if (!state.data) return [];
    return state.data.apiEndpoints.filter((ep: any) => ep.platform_id === platformId);
  },

  getPlatformById: (platformId: number) => {
    const state = get();
    if (!state.data) return null;
    return state.data.platforms.find((p: any) => p.id === platformId) || null;
  },

  getJobTypeById: (jobTypeId: number) => {
    const state = get();
    if (!state.data) return null;
    return state.data.jobTypes.find((jt: any) => jt.id === jobTypeId) || null;
  },

  validatePlatformJobTypeRelationship: (sourcePlatformId: number, targetPlatformId: number, jobTypeId: number) => {
    const { getJobTypeById } = get();
    const jobType = getJobTypeById(jobTypeId);
    if (!jobType) return false;
    
    return jobType.source_platform_id === sourcePlatformId && 
           jobType.target_platform_id === targetPlatformId;
  },

  // Individual data accessors
  getPlatforms: () => {
    const state = get();
    return state.data?.platforms || [];
  },

  getJobTypes: () => {
    const state = get();
    return state.data?.jobTypes || [];
  },

  getGroups: () => {
    const state = get();
    return state.data?.groups || [];
  },

  getUserInfo: () => {
    const state = get();
    return state.data?.userInfo || null;
  },

  getSchemas: () => {
    const state = get();
    return state.data?.schemas || [];
  },

  getYamlConfigs: () => {
    const state = get();
    return state.data?.yamlConfigs || [];
  },

  getApiEndpoints: () => {
    const state = get();
    return state.data?.apiEndpoints || [];
  },

  getExtractors: () => {
    const state = get();
    return state.data?.extractors || [];
  },

  getLoaders: () => {
    const state = get();
    return state.data?.loaders || [];
  }
}));

// Utility hook for components that need to ensure preload is initialized
export const useEnsurePreload = (options?: PreloadOptions) => {
  const { isInitialized, isLoading, error, initializePreload } = usePreloadStore();

  React.useEffect(() => {
    // Only attempt preload if user is authenticated
    if (authService.isAuthenticated() && !isInitialized && !isLoading) {
      initializePreload(options).catch(console.error);
    }
  }, [isInitialized, isLoading, initializePreload]);

  // Return authentication-aware state
  const isAuthenticated = authService.isAuthenticated();
  return { 
    isInitialized: isAuthenticated ? isInitialized : true, // Consider "initialized" if not authenticated
    isLoading: isAuthenticated ? isLoading : false, // No loading if not authenticated
    error: isAuthenticated ? error : null 
  };
};

// React import for useEffect
import React from 'react';